import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookConsultModal from '../components/BookConsultModal';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Smartphone, 
  Shield, 
  Settings, 
  Lock, 
  RefreshCw, 
  Users, 
  CheckCircle2,
  Apple,
  Calendar
} from 'lucide-react';

const MDMPage = () => {
  const [showModal, setShowModal] = useState(false);

  const features = [
    {
      icon: Apple,
      title: 'Apple Business Manager',
      description: 'Zero-touch deployment for all Apple devices. Devices are automatically configured and enrolled the moment they\'re unboxed.'
    },
    {
      icon: Shield,
      title: 'Mosyle MDM',
      description: 'Industry-leading mobile device management platform specifically designed for Apple devices in business environments.'
    },
    {
      icon: Lock,
      title: 'Security Policies',
      description: 'Enforce passcodes, encryption, and security configurations across all managed devices automatically.'
    },
    {
      icon: RefreshCw,
      title: 'Automatic Updates',
      description: 'Keep all devices up-to-date with the latest security patches and OS updates without user intervention.'
    },
    {
      icon: Settings,
      title: 'App Management',
      description: 'Deploy, update, and remove apps remotely. Push business apps silently to devices.'
    },
    {
      icon: Users,
      title: 'User Management',
      description: 'Easily onboard new employees and offboard departing staff with device wipes and reassignment.'
    }
  ];

  const benefits = [
    'Zero-touch device enrollment - devices configure themselves out of the box',
    'Centralized management of all iPhones, iPads, and Macs',
    'Remote lock and wipe for lost or stolen devices',
    'Enforce security policies and compliance requirements',
    'Deploy apps and updates without user intervention',
    'Detailed inventory and reporting on all devices',
    'Reduce IT support tickets with self-service options',
    'Seamless integration with Apple Business Manager'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Smartphone className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-semibold text-cyan-400 uppercase tracking-wide">
                  MDM Specialty
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Mobile Device Management with{' '}
                <span className="text-cyan-400">Apple & Mosyle</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Streamline your Apple device fleet with enterprise-grade MDM. 
                Zero-touch deployment, automated security, and complete control 
                over iPhones, iPads, and Macs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  onClick={() => setShowModal(true)}
                  data-testid="mdm-hero-consult"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book a Free Consult
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => window.location.href = 'tel:+17746447101'}
                >
                  Call 774-644-7101
                </Button>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="w-72 h-72 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl absolute" />
                <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="flex items-center justify-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center">
                      <Apple className="w-10 h-10 text-gray-900" />
                    </div>
                    <span className="text-white text-2xl">+</span>
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                      <span className="text-white font-bold text-xl">M</span>
                    </div>
                  </div>
                  <p className="text-center text-white font-semibold">Apple Business Manager</p>
                  <p className="text-center text-gray-400">+ Mosyle MDM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is MDM Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What is Mobile Device Management?</h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Mobile Device Management (MDM) allows your business to centrally manage, secure, and monitor 
            all company-owned Apple devices. With Apple Business Manager and Mosyle, we provide a complete 
            solution that automates device setup, enforces security policies, and gives you full visibility 
            into your device fleet - all while making life easier for your employees.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Complete Apple Device Management</h2>
            <p className="text-lg text-gray-600">Everything you need to manage your Apple ecosystem</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border border-gray-200 hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Why Choose Our MDM Solution?</h2>
              <p className="text-blue-100 text-lg mb-8">
                We specialize in Apple device management using industry-leading tools. 
                Our expertise with Apple Business Manager and Mosyle means your devices 
                are always secure, up-to-date, and ready to work.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-white mb-4">Devices We Manage</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl">
                  <Smartphone className="w-8 h-8 text-white" />
                  <div>
                    <p className="text-white font-semibold">iPhone</p>
                    <p className="text-blue-200 text-sm">All models supported</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.5 0h-14A3.5 3.5 0 001 3.5v17A3.5 3.5 0 004.5 24h14a3.5 3.5 0 003.5-3.5v-17A3.5 3.5 0 0018.5 0zM12 22.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zM19 18H5V3h14v15z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold">iPad</p>
                    <p className="text-blue-200 text-sm">iPad, iPad Pro, iPad Air, iPad mini</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 3H4a2 2 0 00-2 2v10a2 2 0 002 2h7v2H8v2h8v-2h-3v-2h7a2 2 0 002-2V5a2 2 0 00-2-2zm0 12H4V5h16v10z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold">Mac</p>
                    <p className="text-blue-200 text-sm">MacBook, MacBook Pro, MacBook Air, iMac, Mac mini, Mac Pro</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.25 10a.75.75 0 01.75.75v2.5a.75.75 0 01-1.5 0v-2.5a.75.75 0 01.75-.75zM7.5 10.75a.75.75 0 00-1.5 0v2.5a.75.75 0 001.5 0v-2.5zM12 10a3 3 0 100 6 3 3 0 000-6z"/>
                      <path fillRule="evenodd" d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zM2.5 12a9.5 9.5 0 1119 0 9.5 9.5 0 01-19 0z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold">Apple TV</p>
                    <p className="text-blue-200 text-sm">Conference rooms & digital signage</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Simplify Your Apple Device Management?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Let us show you how MDM can save time, improve security, and make device management effortless.
          </p>
          <Button 
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => setShowModal(true)}
            data-testid="mdm-cta-consult"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Book a Free Consult
          </Button>
        </div>
      </section>

      <Footer />
      <BookConsultModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default MDMPage;
